import { InfoAlert } from '@/components/alerts';
import { LogoutButton } from '@/components/buttons';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useCurrentUserSubscription } from '@/hooks/use-current-user-subscription/hook';
import { PrimaryLayout } from '@/layouts/primary';
import { TopNavFor } from '@/layouts/primary/top-nav';
import { Alert, Button, Flex, Table, Text } from '@mantine/core';
import { IconInfoCircle, IconRosetteDiscountCheck } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { useGetUserSubscriptionChargesQuery } from './graphql.generated';
import styles from './styles.module.css';

export const BillingPage = () => {
	const logoutButton = useMemo(() => <LogoutButton />, []);

	const { data } = useGetUserSubscriptionChargesQuery();
	const { currentUserSubscription } = useCurrentUserSubscription();

	return (
		<PrimaryLayout
			header={{ title: 'Account', backTo: '/account', children: logoutButton }}
			topNav={TopNavFor.ACCOUNT}
		>
			<ContentPanel hasTopNav width="md" gap="md">
				<CustomContentSection
					location={ContentSectionLocation.Billing}
					placement={ContentSectionPlacement.Top}
				/>
				{currentUserSubscription && (
					<InfoAlert
						icon={<IconRosetteDiscountCheck />}
						classNames={{ wrapper: styles.alertWrapper }}
					>
						<Text fz="sm" fw={600}>
							Your Curby {currentUserSubscription.name} subscription is active.
						</Text>
					</InfoAlert>
				)}

				<Flex direction="column" gap="md" w="100%">
					<Text fz="24px" fw={900}>
						Billing History
					</Text>
					{data?.userSubscriptionCharges?.length === 0 && (
						<Alert icon={<IconInfoCircle />} variant="filled" bg="blue.0" mb="md">
							<Text size="sm">
								You currently have no receipts. Once created, your receipts will appear here.
							</Text>
						</Alert>
					)}
					{data?.userSubscriptionCharges && data?.userSubscriptionCharges?.length > 0 && (
						<Table aria-label="Invoices">
							<Table.Thead hidden aria-hidden={false}>
								<Table.Tr>
									<Table.Th>Name</Table.Th>
									<Table.Th>Date</Table.Th>
									<Table.Th>View invoice</Table.Th>
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>
								{data?.userSubscriptionCharges?.map((charge) => (
									<Table.Tr key={charge?.id} className={styles.row}>
										<Table.Td>
											<Text fw={600}>Invoice {charge?.receiptNumber}</Text>
										</Table.Td>
										<Table.Td>
											{charge?.created &&
												DateTime.fromSeconds(charge.created).toFormat('dd/MM/yyyy')}
										</Table.Td>
										<Table.Td>
											{charge?.receiptUrl && (
												<Flex justify="flex-end" w="100%">
													<Button component="a" href={charge?.receiptUrl} target="_blank">
														View
													</Button>
												</Flex>
											)}
										</Table.Td>
									</Table.Tr>
								))}
							</Table.Tbody>
						</Table>
					)}
				</Flex>

				<CustomContentSection
					location={ContentSectionLocation.Billing}
					placement={ContentSectionPlacement.Middle}
				/>
				<CustomContentSection
					location={ContentSectionLocation.Billing}
					placement={ContentSectionPlacement.Bottom}
				/>
			</ContentPanel>
		</PrimaryLayout>
	);
};
